import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeContext, type ThemeContextType } from '@/contexts/ThemeContext';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { getStoredTheme, setStoredTheme } from '@/utils/storage';
import type { UserInfo } from '@/types';

const DEFAULT_THEME = 'light';

// Computes the starting theme when ThemeProvider is first mounted. (It's mounted once per user.)
const getInitialTheme = (userInfo?: UserInfo | null) => {
  // If we have stored a theme for this user Id, it has precedence over user preferences.
  const storedTheme = getStoredTheme(userInfo?.id);
  if (storedTheme) {
    return storedTheme;
  }
  // If the theme was passed in with the user preferences, use it. Otherwise use default.
  return userInfo?.theme || DEFAULT_THEME;
};

/**
 * A Context Provider to enable descendant nodes to access the ThemeContext.
 * Exposes the name of the current theme and helpers to change the theme.
 * Responsible for storing the theme in local storage and applying the theme
 * as a class to the HTML element.
 * On first mount, it will check for a stored theme in local storage,
 * and if not found, it will use the user's preferences or the default theme.
 */
export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userInfo } = useContext(UserInfoContext);
  const [theme, setTheme] = useState(() => getInitialTheme(userInfo));

  // Updates the local state and stores the value in local storage.
  const setAndStoreTheme = useCallback(
    (themeOrUpdater: string | ((prev: string) => string)) => {
      setTheme(prev => {
        const nextTheme =
          typeof themeOrUpdater === 'function' ? themeOrUpdater(prev) : themeOrUpdater;
        setStoredTheme(userInfo?.id, nextTheme);
        return nextTheme;
      });
    },
    [userInfo?.id]
  );

  // Convenient helper to toggle between the 2 themes.
  const toggleTheme = useCallback(
    () => setAndStoreTheme(prev => (prev === 'light' ? 'dark' : 'light')),
    [setAndStoreTheme]
  );

  // Applies the theme as a class to the HTML element.
  // This is done in useLayoutEffect to avoid a flash of the default theme.
  useLayoutEffect(() => {
    try {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (err) {
      console.error('Unable to apply theme to HTML.', err);
    }
  }, [theme]);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      setTheme: setAndStoreTheme,
      toggleTheme,
    }),
    [theme, setAndStoreTheme, toggleTheme]
  );
  return <ThemeContext value={value}>{children}</ThemeContext>;
};

ThemeProvider.displayName = 'ThemeProvider';
